import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, BookOpen, LogOut, Bot, Video, User, Users } from "lucide-react";
import { getAuth, signOut } from "firebase/auth";
import { app } from "../components/Admin/Config/Config";
import { FaWhatsapp } from "react-icons/fa";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const Sidebar = ({ isSidebarOpen, setSidebarState, isSidebarCollapsed }) => {
  const navigate = useNavigate();

  const [onlineCount, setOnlineCount] = useState(12);
  const [pulseScale, setPulseScale] = useState(1);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  const handleNavigation = (to) => {
    navigate(to);
    setSidebarState(false);
  };


  const Logout = () => {
    const auth = getAuth(app);
    signOut(auth)
      .then(() => {
        window.location.href = "/";
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  }

  const menuItems = [
    { to: "/", icon: Home, text: "Courses" },
    { to: "/book", icon: BookOpen, text: "Books" },
    { to: "/clipchart", icon: Video, text: "Clip Chat" },
    { to: "/ai", icon: Bot, text: "Ai Bot" },
    { to: "/profile", icon: User, text: "Profile" },
  ];
  useEffect(() => {
    // Simulate online count fluctuation
    const countInterval = setInterval(() => {
      setOnlineCount(prev => prev + Math.floor(Math.random() * 3) - 1);
    }, 3000);

    // Pulse animation for active status
    const pulseInterval = setInterval(() => {
      setPulseScale(prev => prev === 1 ? 1.2 : 1);
    }, 1000);

    return () => {
      clearInterval(countInterval);
      clearInterval(pulseInterval);
    };
  }, []);

  return (
    <aside
      className={`${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
      ${isSidebarCollapsed ? "hidden" : "block"}
      w-72 bg-black fixed top-0 left-0 z-30 h-[100dvh] 
      transition-all duration-300 ease-in-out border-r border-gray-900/60
      xl:translate-x-0 xl:sticky xl:top-0 xl:h-full shadow-lg
      flex flex-col`}
    >
      {/* Main content area */}
      <div className="flex flex-col h-full">
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain custom-scrollbar pb-4">
          <nav className="p-2 mt-20 xl:mt-6">
            <ul className="space-y-3">
              {menuItems.map((item) => (
                <li key={item.to} className={item.className}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-xl mx-2 cursor-pointer
                       transition-all duration-300 group relative
                       border-2 border-emerald-400/40 shadow-[0_0_10px_rgba(52,211,153,0.3)]
                       ${isActive
                        ? "text-white bg-emerald-700/70 font-medium border-l-4 border-emerald-700 shadow-lg shadow-emerald-500/50"
                        : "text-gray-200 hover:bg-emerald-600/30 hover:text-emerald-400 hover:translate-x-1 hover:shadow-[0_0_15px_rgba(52,211,153,0.5)]"
                      }
                       active:scale-95 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50`
                    }
                    onClick={() => {
                      setSidebarState(false);
                    }}
                  >
                    {({ isActive }) => (
                      <>
                        <div className={`p-1.5 rounded-lg transition-all duration-300 ${isActive ? "bg-emerald-600/20" : "group-hover:bg-emerald-500/10"
                          }`}>
                          <item.icon
                            className={`w-5 h-5 ${isActive ? "stroke-[2.5]" : ""} ${isActive ? "text-white" : "group-hover:text-emerald-400"
                              }`}
                          />
                        </div>
                        <span className="text-sm font-medium">{item.text}</span>

                        {/* Hover arrow */}
                        <div className={`ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isActive ? "text-emerald-300" : "text-emerald-400"
                          }`}>
                          <div className="w-1.5 h-1.5 border-r-2 border-t-2 rotate-45"></div>
                        </div>
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* BRISTU TALK Section - Matching the provided design */}
            <div className="mx-3 my-6 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl overflow-hidden shadow-lg border border-teal-100">
              {/* Header with BRISTU TALK badge */}
              <div className="text-center pt-4 pb-2">
                <div className="inline-block bg-teal-700 text-white px-6 py-2 rounded-full text-sm font-bold tracking-wide">
                  BRISTU TALK
                </div>
              </div>

              {/* Main content */}
              <div className="px-4 pb-4">
                {/* Title text */}
                <div className="text-center mb-4">
                  <h2 className="text-lg font-bold text-slate-800 leading-tight">
                    Ready to talk?
                  </h2>
                  <h3 className="text-lg font-bold text-slate-800 leading-tight">
                    Find your partner!
                  </h3>
                </div>

                {/* Working DotLottie Animations */}
                <div className="flex justify-center mb-4 relative">
                  <div className="relative">
                    {/* Main calling/conversation animation */}
                    <div className="w-32 h-32 relative">
                      <DotLottieReact
                        //  src="https://lottie.host/29d57395-48e6-4b95-ab9c-622eb4bba746/8HkDrSHOzY.lottie"
                        src="https://lottie.host/122eb2fa-4bc4-498a-b104-f9b217bd3015/hLBWKoWSam.lottie"
                        loop
                        autoplay
                        className="w-full h-full"
                      />
                    </div>



                  </div>

                  {/* Fallback with exact character design */}

                </div>

                {/* Stats and Join button */}
                <div className="bg-teal-700 rounded-xl p-3 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-center">
                      <div className="text-xl font-bold">{onlineCount}+</div>
                      <div className="text-xs opacity-90">Online</div>
                    </div>
                    <div className="w-px h-8 bg-teal-500"></div>
                    <div className="flex items-center">
                      <div className={`w-3 h-3 bg-green-400 rounded-full mr-2 transition-transform duration-1000`}
                        style={{ transform: `scale(${pulseScale})` }}></div>
                      <div>
                        <div className="text-sm font-semibold">Active</div>
                        <div className="text-xs opacity-90">Active</div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleNavigation("/bristu-talk")}
                    className="w-full bg-teal-900 hover:bg-teal-800 text-white py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 hover:scale-105 active:scale-95"
                  >
                    <Users className="w-4 h-4" />
                    Join Conversation
                  </button>
                </div>
              </div>
            </div>

            {/* WhatsApp Support Section */}
            <div className="mx-3 mt-6 group mb-6">
              <a
                href="https://wa.me/919495759782"
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex items-center gap-3 p-3 overflow-hidden transition-all duration-300 rounded-xl bg-gray-900/80 hover:bg-gray-800/80 border border-gray-700 hover:border-green-500/50 backdrop-blur-sm"
              >
                <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/30">
                  <FaWhatsapp className="w-4 h-4 text-green-400" />
                </div>

                <div className="flex-1 text-left">
                  <h4 className="text-sm font-semibold text-gray-100">
                    WhatsApp Support
                  </h4>
                  <p className="text-xs text-gray-400">
                    Available 24/7
                  </p>
                </div>
              </a>
            </div>
          </nav>
        </div>

        {/* Logout Section - Fixed at bottom */}
        <div className="mt-auto border-t border-gray-800/60 bg-black pb-safe">
          <div className="p-2 mx-3 mt-2 mb-2 rounded-xl">
            <button
              onClick={Logout}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  Logout();
                }
              }}
              tabIndex={0}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-gray-600 
                hover:text-emerald-600 bg-emerald-50 text-emerald-600 hover:bg-green-50 rounded-lg
                transition-all duration-200 group hover:scale-[1.02] active:scale-[0.98]
                focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50"
            >
              <LogOut className="w-5 h-5 transition-transform group-hover:rotate-12" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Animation styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(16, 185, 129, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(16, 185, 129, 0.5);
        }
      `}</style>
    </aside>
  );
};

export default Sidebar;